import { ARTICLE_DEFAULT_LABEL, ARTICLE_LIMITS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Icon } from "@sagot/ui";
import type { ShowArticleStep } from "../types";
import { useCrawlPages } from "../use-article-sources";
import { ArticlePageField } from "./article-page-field";
import { ArticlePreview } from "./article-preview";
import { ArticleSourceField } from "./article-source-field";
import { StepChannelNotes } from "./channel-notes";
import { CountedInput, CountedTextarea } from "./counted-field";
import { nameOf, useRefs } from "./refs";
import { VariableList, type FormProps } from "./webhook-form";

const A = "engage.workflows.article";

/** Without a text and a label the step still works: the card has its title and the server's button words. */
function titleOf(step: ShowArticleStep, sourceName: string, pageTitle: string | null | undefined): string {
  return step.page_id !== undefined && pageTitle ? pageTitle : sourceName;
}

/**
 * Show article: a card that links a website from Knowledge (or one page of it) in a new tab. Only websites with a public
 * https address can be chosen; the card is skipped for a visitor the source is not open to, and the workflow carries on.
 */
export function ArticleForm({ step, disabled, onChange }: FormProps<ShowArticleStep>) {
  const { t } = useTranslation();
  const refs = useRefs();
  const pages = useCrawlPages(step.source_id, "", step.page_id !== undefined);
  const pageTitle = pages.data?.find((page) => page.id === step.page_id)?.title;
  const sourceName = step.source_id === "" ? t(`${A}.previewTitle`) : nameOf(refs.sources ?? [], step.source_id, t(`${A}.previewTitle`));
  return (
    <div className="space-y-5">
      <p className="text-xs text-fg-subtle">{t(`${A}.intro`)}</p>
      <ArticleSourceField
        value={step.source_id}
        disabled={disabled}
        onChange={(source_id) => { onChange({ id: step.id, name: step.name, type: "show_article", source_id, text: step.text, label: step.label }); }}
      />
      {step.source_id !== "" ? (
        <ArticlePageField key={step.source_id} sourceId={step.source_id} value={step.page_id} disabled={disabled} onChange={(page_id) => { onChange({ ...step, page_id }); }} />
      ) : null}
      <div>
        <CountedTextarea label={t(`${A}.text`)} hint={t(`${A}.textHint`)} rows={3} value={step.text ?? ""} max={ARTICLE_LIMITS.textMax} disabled={disabled} onChange={(text) => { onChange({ ...step, text: text || undefined }); }} />
        <VariableList />
      </div>
      <CountedInput label={t(`${A}.label`)} hint={t(`${A}.labelHint`)} value={step.label ?? ""} max={ARTICLE_LIMITS.labelMax} placeholder={ARTICLE_DEFAULT_LABEL.en} disabled={disabled} onChange={(label) => { onChange({ ...step, label: label || undefined }); }} />
      <p className="flex items-start gap-2 rounded-md bg-surface-2 p-3 text-xs text-fg-muted">
        <Icon name="shield-check" className="mt-0.5 size-3.5 shrink-0" />
        {t(`${A}.audience`)}
      </p>
      <ArticlePreview title={titleOf(step, sourceName, pageTitle)} text={step.text ?? ""} label={step.label ?? ""} />
      <StepChannelNotes type="show_article" />
    </div>
  );
}
